import React, { useState, useMemo } from 'react';
import { Input } from './input';
import { Button } from './button';
import { Checkbox } from './checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import { 
  ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight, 
  ChevronsLeft, ChevronsRight, Search, X, Filter, Printer, Download,
  CheckSquare, Square, Trash2
} from 'lucide-react';
import { cn } from '../../lib/utils';
import * as XLSX from 'xlsx';

/**
 * DataTable - Tabel avansat cu filtrare, sortare, paginare si selectie multipla
 * 
 * Props:
 * - data: Array de obiecte de afisat
 * - columns: Array de configuratii coloane [{key, label, sortable, filterable, render, width}]
 * - pageSize: Numar initial de randuri per pagina (default: 10)
 * - pageSizeOptions: Array de optiuni pentru randuri per pagina
 * - searchable: Boolean - afiseaza caseta de cautare globala
 * - exportable: Boolean - afiseaza buton export
 * - printable: Boolean - afiseaza buton print
 * - selectable: Boolean - afiseaza checkbox-uri pentru selectie
 * - onRowClick: Functie callback la click pe rand
 * - onSelectionChange: Functie callback cand selectia se schimba
 * - onBulkDelete: Functie callback pentru stergere in masa
 * - className: Clase CSS aditionale
 * - idField: Campul folosit ca ID unic (default: 'id')
 */
export const DataTable = ({
  data = [],
  columns = [],
  pageSize: initialPageSize = 10,
  pageSizeOptions = [5, 10, 20, 50, 100],
  searchable = true,
  exportable = false,
  printable = false,
  selectable = false,
  onRowClick,
  onSelectionChange,
  onBulkDelete,
  className,
  emptyMessage = "Nu exista date de afisat",
  idField = 'id'
}) => {
  // State pentru sortare
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  // State pentru filtrare globala
  const [globalSearch, setGlobalSearch] = useState('');
  
  // State pentru filtrare per coloana
  const [columnFilters, setColumnFilters] = useState({});
  const [showColumnFilters, setShowColumnFilters] = useState(false);
  
  // State pentru paginare
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  // State pentru selectie
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Filtrare date
  const filteredData = useMemo(() => {
    let result = [...data];
    
    // Filtrare globala
    if (globalSearch) {
      const searchLower = globalSearch.toLowerCase();
      result = result.filter(row => 
        columns.some(col => {
          const value = row[col.key];
          if (value == null) return false;
          return String(value).toLowerCase().includes(searchLower);
        })
      );
    }
    
    // Filtrare per coloana
    Object.entries(columnFilters).forEach(([key, filterValue]) => {
      if (filterValue) {
        const filterLower = filterValue.toLowerCase();
        result = result.filter(row => {
          const value = row[key];
          if (value == null) return false;
          return String(value).toLowerCase().includes(filterLower);
        });
      }
    });
    
    return result;
  }, [data, globalSearch, columnFilters, columns]);

  // Sortare date
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;
    
    return [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      
      // Detectare tip si sortare corespunzatoare
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      // Sortare string
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      
      if (sortConfig.direction === 'asc') {
        return aStr.localeCompare(bStr, 'ro');
      }
      return bStr.localeCompare(aStr, 'ro');
    });
  }, [filteredData, sortConfig]);

  // Paginare
  const totalPages = Math.ceil(sortedData.length / pageSize);
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return sortedData.slice(startIndex, startIndex + pageSize);
  }, [sortedData, currentPage, pageSize]);

  // Reset pagina la schimbare filtre
  React.useEffect(() => {
    setCurrentPage(1);
  }, [globalSearch, columnFilters, pageSize]);

  // Notifica parintele despre schimbarea selectiei
  // Only trigger when selectedIds changes, not when data changes
  const prevSelectedIdsRef = React.useRef(selectedIds);
  React.useEffect(() => {
    if (onSelectionChange && prevSelectedIdsRef.current !== selectedIds) {
      prevSelectedIdsRef.current = selectedIds;
      // Send array of IDs, not objects
      onSelectionChange(Array.from(selectedIds));
    }
  }, [selectedIds, onSelectionChange]);

  // Handler sortare
  const handleSort = (key) => {
    const column = columns.find(c => c.key === key);
    if (!column?.sortable) return;
    
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Handler filtrare coloana
  const handleColumnFilter = (key, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Clear toate filtrele
  const clearAllFilters = () => {
    setGlobalSearch('');
    setColumnFilters({});
  };

  // Handler selectie rand
  const handleSelectRow = (id) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Handler selectie toate randurile din pagina curenta
  const handleSelectAllPage = () => {
    const pageIds = paginatedData.map(row => row[idField]);
    const allSelected = pageIds.every(id => selectedIds.has(id));
    
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (allSelected) {
        pageIds.forEach(id => newSet.delete(id));
      } else {
        pageIds.forEach(id => newSet.add(id));
      }
      return newSet;
    });
  };

  // Handler selectie toate randurile filtrate
  const handleSelectAll = () => {
    const allIds = sortedData.map(row => row[idField]);
    const allSelected = allIds.length > 0 && allIds.every(id => selectedIds.has(id));
    
    setSelectedIds(prev => {
      if (allSelected) {
        return new Set();
      } else {
        return new Set(allIds);
      }
    });
  };

  // Clear selectia
  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  // Datele pentru export (selectate sau toate filtrate)
  const getExportData = () => {
    if (selectedIds.size > 0) {
      return sortedData.filter(row => selectedIds.has(row[idField]));
    }
    return sortedData;
  };

  // Export Excel
  const handleExport = () => {
    const exportData = getExportData();
    const exportColumns = columns.filter(c => c.key !== 'actions');
    
    // Prepare data for Excel
    const excelData = exportData.map(row => {
      const rowData = {};
      exportColumns.forEach(col => {
        rowData[col.label] = row[col.key] ?? '';
      });
      return rowData;
    });
    
    // Create worksheet and workbook
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Export');
    
    // Auto-width columns
    const maxWidths = {};
    exportColumns.forEach(col => {
      maxWidths[col.label] = Math.max(col.label.length, 10);
    });
    excelData.forEach(row => {
      Object.keys(row).forEach(key => {
        const len = String(row[key]).length;
        if (len > maxWidths[key]) maxWidths[key] = Math.min(len, 50);
      });
    });
    ws['!cols'] = Object.values(maxWidths).map(w => ({ wch: w }));
    
    // Download
    const filename = `export_${selectedIds.size > 0 ? `${selectedIds.size}_selectate_` : ''}${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  // Print
  const handlePrint = () => {
    const exportData = getExportData();
    const printWindow = window.open('', '_blank');
    
    const tableHTML = `
      <table style="width: 100%; border-collapse: collapse; font-family: Arial, sans-serif;">
        <thead>
          <tr>
            ${columns.filter(c => c.key !== 'actions').map(c => 
              `<th style="border: 1px solid #ddd; padding: 8px; text-align: left; background-color: #0052CC; color: white;">${c.label}</th>`
            ).join('')}
          </tr>
        </thead>
        <tbody>
          ${exportData.map((row, idx) => `
            <tr style="background-color: ${idx % 2 === 0 ? '#fff' : '#f9f9f9'};">
              ${columns.filter(c => c.key !== 'actions').map(col => 
                `<td style="border: 1px solid #ddd; padding: 8px;">${row[col.key] ?? ''}</td>`
              ).join('')}
            </tr>
          `).join('')}
        </tbody>
      </table>
    `;
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Print - ${selectedIds.size > 0 ? `${selectedIds.size} selectate` : 'Toate'}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <h2 style="color: #0052CC; margin-bottom: 20px;">
            ${selectedIds.size > 0 ? `${selectedIds.size} inregistrari selectate` : `${exportData.length} inregistrari`}
          </h2>
          ${tableHTML}
          <p style="font-size: 12px; color: #666; margin-top: 20px;">
            Generat la: ${new Date().toLocaleString('ro-RO')}
          </p>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // Icon sortare
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ChevronsUpDown className="w-4 h-4 text-slate-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ChevronUp className="w-4 h-4 text-blue-600" />
      : <ChevronDown className="w-4 h-4 text-blue-600" />;
  };

  const hasActiveFilters = globalSearch || Object.values(columnFilters).some(v => v);
  const pageIds = paginatedData.map(row => row[idField]);
  const allPageSelected = pageIds.length > 0 && pageIds.every(id => selectedIds.has(id));
  const somePageSelected = pageIds.some(id => selectedIds.has(id));

  return (
    <div className={cn("space-y-4", className)}>
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Cautare globala */}
          {searchable && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Cauta..."
                value={globalSearch}
                onChange={(e) => setGlobalSearch(e.target.value)}
                className="pl-9 w-48 sm:w-64 h-9"
                data-testid="table-global-search"
              />
              {globalSearch && (
                <button 
                  onClick={() => setGlobalSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  <X className="w-4 h-4 text-slate-400 hover:text-slate-600" />
                </button>
              )}
            </div>
          )}
          
          {/* Toggle filtre coloane */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowColumnFilters(!showColumnFilters)}
            className={cn(
              "h-9",
              showColumnFilters && "bg-blue-50 border-blue-300"
            )}
            data-testid="table-column-filters-toggle"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filtre
          </Button>
          
          {/* Clear filters */}
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAllFilters}
              className="h-9 text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <X className="w-4 h-4 mr-1" />
              Sterge filtre
            </Button>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {/* Selection info and actions */}
          {selectable && selectedIds.size > 0 && (
            <div className="flex items-center gap-2 px-3 py-1 bg-blue-50 rounded-lg border border-blue-200">
              <CheckSquare className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-700">{selectedIds.size} selectate</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearSelection}
                className="h-7 px-2 text-blue-600 hover:text-blue-800"
              >
                <X className="w-4 h-4" />
              </Button>
              {onBulkDelete && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onBulkDelete(Array.from(selectedIds))}
                  className="h-7 px-2 text-red-600 hover:text-red-800 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          )}
          
          {/* Export */}
          {exportable && (
            <Button variant="outline" size="sm" onClick={handleExport} className="h-9" data-testid="table-export-btn">
              <Download className="w-4 h-4 mr-2" />
              Export {selectedIds.size > 0 ? `(${selectedIds.size})` : ''}
            </Button>
          )}
          
          {/* Print */}
          {printable && (
            <Button variant="outline" size="sm" onClick={handlePrint} className="h-9" data-testid="table-print-btn">
              <Printer className="w-4 h-4 mr-2" />
              Print {selectedIds.size > 0 ? `(${selectedIds.size})` : ''}
            </Button>
          )}
        </div>
      </div>

      {/* Selection toolbar */}
      {selectable && sortedData.length > 0 && (
        <div className="flex items-center gap-4 text-sm">
          <button
            onClick={handleSelectAll}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
          >
            {selectedIds.size === sortedData.length ? (
              <CheckSquare className="w-4 h-4 text-blue-600" />
            ) : (
              <Square className="w-4 h-4" />
            )}
            Selecteaza toate ({sortedData.length})
          </button>
          {selectedIds.size > 0 && selectedIds.size < sortedData.length && (
            <span className="text-slate-400">|</span>
          )}
          {selectedIds.size > 0 && (
            <button
              onClick={clearSelection}
              className="text-red-600 hover:text-red-700"
            >
              Deselecteaza tot
            </button>
          )}
        </div>
      )}

      {/* Tabel */}
      <div className="border rounded-lg overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <table id="data-table-print" className="w-full">
            <thead>
              {/* Header sortare */}
              <tr className="bg-slate-50 border-b">
                {selectable && (
                  <th className="px-3 py-3 w-10">
                    <Checkbox
                      checked={allPageSelected}
                      onCheckedChange={handleSelectAllPage}
                      className={somePageSelected && !allPageSelected ? "data-[state=checked]:bg-blue-300" : ""}
                    />
                  </th>
                )}
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={cn(
                      "px-4 py-3 text-left text-sm font-semibold text-slate-700",
                      col.sortable && "cursor-pointer select-none hover:bg-slate-100",
                      col.width
                    )}
                    onClick={() => handleSort(col.key)}
                    data-testid={`table-header-${col.key}`}
                  >
                    <div className="flex items-center gap-2">
                      <span>{col.label}</span>
                      {col.sortable && getSortIcon(col.key)}
                    </div>
                  </th>
                ))}
              </tr>
              
              {/* Filtre coloane */}
              {showColumnFilters && (
                <tr className="bg-slate-50/50 border-b">
                  {selectable && <th className="px-3 py-2"></th>}
                  {columns.map((col) => (
                    <th key={`filter-${col.key}`} className="px-4 py-2">
                      {col.filterable !== false && col.key !== 'actions' ? (
                        <Input
                          placeholder={`Filtreaza...`}
                          value={columnFilters[col.key] || ''}
                          onChange={(e) => handleColumnFilter(col.key, e.target.value)}
                          className="h-8 text-sm font-normal"
                          data-testid={`table-filter-${col.key}`}
                        />
                      ) : null}
                    </th>
                  ))}
                </tr>
              )}
            </thead>
            
            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan={columns.length + (selectable ? 1 : 0)} className="px-4 py-12 text-center text-slate-500">
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                paginatedData.map((row, rowIndex) => {
                  const rowId = row[idField];
                  const isSelected = selectedIds.has(rowId);
                  
                  return (
                    <tr
                      key={rowId || rowIndex}
                      className={cn(
                        "border-b last:border-b-0 transition-colors",
                        isSelected ? "bg-blue-50" : rowIndex % 2 === 0 ? "bg-white" : "bg-slate-50/30",
                        onRowClick && "cursor-pointer hover:bg-slate-50"
                      )}
                      onClick={(e) => {
                        // Nu apela onRowClick daca s-a dat click pe checkbox
                        if (e.target.closest('[role="checkbox"]')) return;
                        onRowClick?.(row);
                      }}
                      data-testid={`table-row-${rowIndex}`}
                    >
                      {selectable && (
                        <td className="px-3 py-3">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => handleSelectRow(rowId)}
                          />
                        </td>
                      )}
                      {columns.map((col) => (
                        <td 
                          key={col.key} 
                          className={cn("px-4 py-3 text-sm text-slate-700", col.width)}
                        >
                          {col.render ? col.render(row[col.key], row) : row[col.key]}
                        </td>
                      ))}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Paginare */}
      {sortedData.length > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <span>Afiseaza</span>
            <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
              <SelectTrigger className="w-20 h-8" data-testid="table-page-size">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map(size => (
                  <SelectItem key={size} value={String(size)}>{size}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span>din {sortedData.length} rezultate</span>
            {selectedIds.size > 0 && (
              <span className="text-blue-600 font-medium">({selectedIds.size} selectate)</span>
            )}
          </div>
          
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              data-testid="table-first-page"
            >
              <ChevronsLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              data-testid="table-prev-page"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            
            <div className="flex items-center gap-1 px-2">
              <span className="text-sm text-slate-600">
                Pagina <span className="font-medium">{currentPage}</span> din <span className="font-medium">{totalPages || 1}</span>
              </span>
            </div>
            
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              data-testid="table-next-page"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage >= totalPages}
              data-testid="table-last-page"
            >
              <ChevronsRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
